import React, { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Button,
	Card,
	Form,
	Input,
	Radio,
	Space,
	Spin,
	Typography,
	message,
} from "antd";
import { ArrowLeftOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../utilities/useAxios";
import PageTitle from "../PageTitle";

const { Title, Text } = Typography;

const CourseExamPage = () => {
	const { courseId } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [statusData, setStatusData] = useState(null);
	const [questions, setQuestions] = useState([]);
	const [answers, setAnswers] = useState({});
	const [result, setResult] = useState(null);

	const fetchExamStatus = useCallback(async () => {
		try {
			setLoading(true);
			const statusResponse = await useAxios.get(
				`/cma/courses/${courseId}/exam/status/`,
			);
			setStatusData(statusResponse.data);

			if (statusResponse.data?.can_take_exam) {
				const questionsResponse = await useAxios.get(
					`/cma/courses/${courseId}/exam/questions/`,
				);
				setQuestions(
					questionsResponse.data.questions || [],
				);
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"시험 정보를 불러오는데 실패했습니다.",
			);
		} finally {
			setLoading(false);
		}
	}, [courseId]);

	useEffect(() => {
		fetchExamStatus();
	}, [fetchExamStatus]);

	const handleChoiceChange = (questionId, value) => {
		setAnswers((prev) => ({
			...prev,
			[questionId]: {
				...prev[questionId],
				selected_choice_index: value,
			},
		}));
	};

	const handleTextChange = (questionId, value) => {
		setAnswers((prev) => ({
			...prev,
			[questionId]: {
				...prev[questionId],
				answer_text: value,
			},
		}));
	};

	const handleSubmit = async () => {
		try {
			setSubmitting(true);
			const payload = {
				answers: questions.map((q) => ({
					question_id: q.id,
					selected_choice_index:
						answers[q.id]
							?.selected_choice_index,
					answer_text: answers[q.id]?.answer_text,
				})),
			};
			const response = await useAxios.post(
				`/cma/courses/${courseId}/exam/submit/`,
				payload,
			);
			setResult(response.data);
			message.success("시험 제출이 완료되었습니다.");
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"시험 제출에 실패했습니다.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div style={{ padding: "24px" }}>
			<PageTitle title="기독사관학교 - 시험 응시" />
			<Card style={{ marginBottom: "16px" }}>
				<Space
					style={{
						width: "100%",
						justifyContent: "space-between",
					}}>
					<Title level={3} style={{ margin: 0 }}>
						시험 응시
					</Title>
					<Button
						icon={<ArrowLeftOutlined />}
						onClick={() =>
							navigate("/cma")
						}>
						돌아가기
					</Button>
				</Space>
			</Card>

			{!statusData?.has_exam && (
				<Alert
					type="info"
					message="등록된 시험이 없습니다."
					showIcon
				/>
			)}

			{statusData?.is_submitted && !result && (
				<Alert
					type="success"
					showIcon
					message={`이미 응시 완료했습니다. 점수: ${statusData.score}점`}
					description={`합격 여부: ${
						statusData.is_passed
							? "합격"
							: "불합격"
					}`}
				/>
			)}

			{statusData?.has_exam &&
				!statusData?.can_take_exam &&
				!statusData?.is_submitted && (
					<Alert
						type="warning"
						showIcon
						message="현재 시험 응시 조건을 충족하지 않습니다."
						description="시험 기간/완강 여부를 확인해주세요."
					/>
				)}

			{result && (
				<Card
					style={{
						marginTop: "16px",
						marginBottom: "16px",
					}}>
					<Space direction="vertical">
						<Text strong>
							<CheckCircleOutlined />{" "}
							제출 완료
						</Text>
						<Text>
							총점:{" "}
							{result.total_score}점
						</Text>
						<Text>
							합격 기준:{" "}
							{result.pass_score}점
						</Text>
						<Text strong>
							결과:{" "}
							{result.is_passed
								? "합격"
								: "불합격"}
						</Text>
					</Space>
				</Card>
			)}

			{statusData?.can_take_exam && !result && (
				<Card>
					<Form layout="vertical">
						{questions.map((q) => (
							<Card
								key={q.id}
								size="small"
								style={{
									marginBottom:
										"12px",
								}}
								title={`문항 ${q.order} (${q.score}점)`}>
								<div
									style={{
										whiteSpace: "pre-wrap",
										wordBreak: "break-word",
									}}>
									{q.question_text}
								</div>
								<div
									style={{
										marginTop: "12px",
									}}>
									{q.question_type ===
									"MULTIPLE_CHOICE" ? (
										<Radio.Group
											onChange={(
												e,
											) =>
												handleChoiceChange(
													q.id,
													e
														.target
														.value,
												)
											}
											value={
												answers[
													q
														.id
												]
													?.selected_choice_index
											}>
											<Space direction="vertical">
												{(
													q.choices ||
													[]
												).map(
													(
														choice,
														idx,
													) => (
														<Radio
															key={
																idx
															}
															value={
																idx
															}>
															{
																choice
															}
														</Radio>
													),
												)}
											</Space>
										</Radio.Group>
									) : (
										<Input
											placeholder="답안을 입력하세요"
											value={
												answers[
													q
														.id
												]
													?.answer_text
											}
											onChange={(
												e,
											) =>
												handleTextChange(
													q.id,
													e
														.target
														.value,
												)
											}
										/>
									)}
								</div>
							</Card>
						))}
						<Button
							type="primary"
							onClick={handleSubmit}
							loading={submitting}>
							시험 제출
						</Button>
					</Form>
				</Card>
			)}
		</div>
	);
};

export default CourseExamPage;
